import React from 'react'
import { Link, Tag } from '@chakra-ui/react'

import { useUserContext } from '~config/user-context'
import { MENTION_REGEX } from '~utils/mention-helpers'

interface Props {
    text: string
    /**
     * When true, the chip rendered for the current viewer is highlighted
     * in a more prominent color so they can quickly see where they were
     * mentioned. Defaults to true.
     */
    highlightSelf?: boolean
}

/**
 * Renders message body text with `@[Display Name](user_id)` mention markup
 * resolved into Chakra `Tag` chips, while still linkifying http(s) URLs in
 * the surrounding plain text. Replaces `parseMessageText` in any place
 * that may show mention-marked-up content.
 *
 * Mentions of the current viewer use a brighter color scheme so the user
 * can scan a thread and immediately see where they're referenced.
 */
export default function MentionText({ text, highlightSelf = true }: Props) {
    const { user } = useUserContext()
    const currentUserId = user?.id

    const tokens = tokenize(text)

    return (
        <>
            {tokens.map((token, index) => {
                if (token.kind === 'mention') {
                    const isMe =
                        highlightSelf &&
                        currentUserId !== undefined &&
                        token.userId === currentUserId

                    return (
                        <Tag
                            key={`mention-${index}`}
                            size="sm"
                            colorScheme={isMe ? 'blue' : 'brand'}
                            variant={isMe ? 'solid' : 'subtle'}
                            borderRadius="full"
                            verticalAlign="baseline"
                            mx="1px"
                        >
                            @{token.displayName}
                        </Tag>
                    )
                }

                if (token.kind === 'url') {
                    return (
                        <Link
                            key={`url-${index}`}
                            href={token.url}
                            color="inherit"
                            textDecoration="underline"
                            isExternal
                        >
                            {token.url}
                        </Link>
                    )
                }

                return (
                    <React.Fragment key={`text-${index}`}>
                        {token.text}
                    </React.Fragment>
                )
            })}
        </>
    )
}

type TokenizedNode =
    | { kind: 'text'; text: string }
    | { kind: 'mention'; userId: string; displayName: string }
    | { kind: 'url'; url: string }

const URL_REGEX = /(https?:\/\/[^\s]+)/g

/**
 * Splits a string into a flat list of text/mention/url tokens. Mentions
 * are extracted first because their delimiters never overlap with URLs;
 * the remaining plain-text segments are then scanned for http(s) links.
 */
function tokenize(text: string): TokenizedNode[] {
    const out: TokenizedNode[] = []
    let lastIndex = 0

    const mentionMatches = Array.from(text.matchAll(MENTION_REGEX))

    for (const match of mentionMatches) {
        const matchIndex = match.index ?? 0
        if (matchIndex > lastIndex) {
            pushTextWithUrls(out, text.slice(lastIndex, matchIndex))
        }
        out.push({
            kind: 'mention',
            displayName: match[1],
            userId: match[2],
        })
        lastIndex = matchIndex + match[0].length
    }

    if (lastIndex < text.length) {
        pushTextWithUrls(out, text.slice(lastIndex))
    }

    return out
}

function pushTextWithUrls(out: TokenizedNode[], chunk: string): void {
    if (!chunk) return

    let lastIndex = 0
    for (const match of chunk.matchAll(URL_REGEX)) {
        const matchIndex = match.index ?? 0
        if (matchIndex > lastIndex) {
            out.push({ kind: 'text', text: chunk.slice(lastIndex, matchIndex) })
        }
        out.push({ kind: 'url', url: match[0] })
        lastIndex = matchIndex + match[0].length
    }

    if (lastIndex < chunk.length) {
        out.push({ kind: 'text', text: chunk.slice(lastIndex) })
    }
}
